import { notify } from '@/lib/notify';
import { logout } from './session-service';
import { DeletedReservation, AfipVoucher, ReservationPaxGroup, ServiceBookingPaxGroup, Person } from '@turizi/shared';


export interface ApiResponse<T> {
    data?: T;
    rows?: T[];
    count?: number;
    unread_count?: number;
    error?: {
        title?: string;
        message?: string;
    } | string;
}

const BASE_URL = import.meta.env.VITE_API_BASE_URL;

// Fetch wrapper with session-based auth and error handling
const fetchWrapper = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
    const url = `${BASE_URL}${endpoint}`;
    const headers: any = {
        'Content-Type': 'application/json',
        ...options.headers,
    };

    // If body is FormData, delete Content-Type to let browser set it with boundary
    if (options.body instanceof FormData) {
        delete headers['Content-Type'];
    }

    const defaultOptions: RequestInit = {
        credentials: 'include', // Include cookies for session-based auth
        mode: 'cors', // Explicitly set CORS mode
        headers,
        ...options,
    };
    try {
        // Simulate network delay for localhost
        if (window.location.href.includes('localhost')) {
            console.log('simulating network delay');
            await new Promise(resolve => setTimeout(resolve, 1500));
        }
        const response = await fetch(url, defaultOptions);
        if (response.status === 401 && !endpoint.includes('/login')) {
            console.log('logout redirect');
            logout();
            return;
        }
        const data = await response.json();
        if (!response.ok) {
            const errVal = data?.error;
            const errMsg = typeof errVal === 'string'
                ? errVal
                : (errVal?.message || errVal?.title || data?.message || 'Error occurred');
            const error = new Error(errMsg) as any;
            error.response = { data };
            throw error;
        }
        return data;
    } catch (error) {
        throw error;
    }
};

const cleanParams = (params: any) => {
    return Object.fromEntries(
        Object.entries(params)
            .filter(([_, value]) => value !== undefined && value !== null)
            .map(([key, value]) => [key, String(value)])
    );
};

// Helper functions to reduce repetition
const createGetAllMethod = (endpoint: string) => async (params?: any) => {
    const query = params ? `?${new URLSearchParams(cleanParams(params)).toString()}` : '';
    const result = await fetchWrapper(`${endpoint}${query}`);
    return { data: Array.isArray(result) ? result : (result?.rows || result?.data), count: result?.count ?? (Array.isArray(result) ? result.length : undefined), error: result?.error };
};

const createGetOneMethod = (endpoint: string) => async (id: number | string, params?: any) => {
    const query = params ? `?${new URLSearchParams(params).toString()}` : '';
    const result = await fetchWrapper(`${endpoint}/${id}${query}`);
    return { data: result?.data || result, error: result?.error };
};

const createSaveMethod = (endpoint: string, notifyKey?: string) => async (data: any) => {
    const method = data.id !== undefined && data.id !== null ? 'PUT' : 'POST';
    const url = data.id !== undefined && data.id !== null ? `${endpoint}/${data.id}` : endpoint;
    const result = await fetchWrapper(url, {
        method,
        body: JSON.stringify(data),
    });
    if (notifyKey) {
        notify.smart({ data: result }, notifyKey);
    }
    return { data: result?.data || result, error: result?.error };
};

const createDeleteMethod = (endpoint: string) => async (id: number | string) => {
    const result = await fetchWrapper(`${endpoint}/${id}`, {
        method: 'DELETE',
    });
    return { data: result, error: result?.error };
};

const PUBLIC_ENDPOINT = '/public';

const api = {
    // Generic API methods, try to avoid using these
    async post<T>(endpoint: string, data: any): Promise<ApiResponse<T>> {
        const result = await fetchWrapper(endpoint, {
            method: 'POST',
            body: JSON.stringify(data),
        });
        return { data: result, error: result?.error };
    },

    async get<T>(endpoint: string): Promise<T> {
        const result = await fetchWrapper(endpoint);
        return result;
    },

    async upload<T>(endpoint: string, formData: FormData): Promise<ApiResponse<T>> {
        const result = await fetchWrapper(endpoint, {
            method: 'POST',
            body: formData,
        });
        return { data: result, error: result?.error };
    },

    async put<T>(endpoint: string, id: number, data: any): Promise<ApiResponse<T>> {
        const result = await fetchWrapper(`${endpoint}/${id}`, {
            method: 'PUT',
            body: JSON.stringify(data),
        });
        return { data: result, error: result?.error };
    },

    async delete<T>(endpoint: string, id: number): Promise<ApiResponse<T>> {
        const result = await fetchWrapper(`${endpoint}/${id}`, {
            method: 'DELETE',
        });
        return { data: result, error: result?.error };
    },

    async getAll<T>(endpoint: string, params?: any): Promise<ApiResponse<T>> {
        const query = params ? `?${new URLSearchParams(params).toString()}` : '';
        const result = await fetchWrapper(`${endpoint}${query}`);
        return { data: result?.rows, count: result?.count, error: result?.error };
    },

    async getOne<T>(endpoint: string, id?: any, params?: any): Promise<ApiResponse<T>> {
        const query = params ? `?${new URLSearchParams(params).toString()}` : '';
        const result = await fetchWrapper(`${endpoint}/${id}${query}`);
        return { data: result, error: result?.error };
    },

    ////////////////////////////////
    //// API methods for entities ////
    ////////////////////////////////

    auth: {
        me: async () => {
            const result = await fetchWrapper('/me');
            return { data: result, error: result?.error };
        },
        login: async (credentials: any) => {
            const redirectUrl = window.location.origin + '/login';
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/login`, {
                method: 'POST',
                body: JSON.stringify({ ...credentials, redirectUrl }),
            });
            return { data: result, error: result?.error };
        },
        loginPassword: async (credentials: any) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/login-password`, {
                method: 'POST',
                body: JSON.stringify(credentials),
            });
            return { data: result, error: result?.error };
        },
        validateToken: async (token: string) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/login/validate?token=${token}`, {
                method: 'POST',
                body: JSON.stringify({}),
            });
            return { data: result, error: result?.error };
        },
        logout: async () => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/logout`, {
                method: 'POST',
                body: JSON.stringify({}),
            });
            return { data: result, error: result?.error };
        },
        changePassword: async (data: any) => {
            const result = await fetchWrapper('/change-password', {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
    },

    onboarding: {
        check: async (email: string) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/onboarding/check?email=${encodeURIComponent(email)}`);
            return { data: result, error: result?.error };
        },
        confirm: async (email: string, businessId: number) => {
            const redirectUrl = window.location.origin + '/login';
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/onboarding/confirm`, {
                method: 'POST',
                body: JSON.stringify({ email, businessId, redirectUrl }),
            });
            return { data: result, error: result?.error };
        },
    },

    newsletter: {
        subscribe: async (data: { email: string; extra_data?: Record<string, any> }) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/newsletter/subscribe`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        unsubscribe: async (email: string) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/newsletter/unsubscribe`, {
                method: 'POST',
                body: JSON.stringify({ email }),
            });
            return { data: result, error: result?.error };
        },
    },

    public: {
        onboarding: async (data: any) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/onboarding`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        getBusinessBySlug: async (slug: string) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/business/${slug}`);
            return { data: result, error: result?.error };
        },
        getServices: async (params?: any) => {
            let endpoint = `${PUBLIC_ENDPOINT}/services`;
            const query = params ? `?${new URLSearchParams(cleanParams(params)).toString()}` : '';
            const result = await fetchWrapper(`${endpoint}${query}`);
            return { data: result?.rows || result?.data, count: result?.count, error: result?.error };
        },
        getService: async (id?: string) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/services/${id}`);
            return { data: result, error: result?.error };
        },
        getServicePrice: async (id: string | number, params?: any) => {
            const query = params ? `?${new URLSearchParams(params).toString()}` : '';
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/services/${id}/price${query}`);
            return { data: result, error: result?.error };
        },
        getReservation: async (slug: string) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/trip/${slug}`);
            return { data: result, error: result?.error };
        },
        getFilters: async (params = {}) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/filters/services?${new URLSearchParams(params).toString()}`);
            return { data: result, error: result?.error };
        },
        getTenantBusiness: async (tenantBusinessId: string | number) => {
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/tenant-businesses/${tenantBusinessId}`);
            return { data: result, error: result?.error };
        },
        getTenant: async (tenantId: string, params?: Record<string, any>) => {
            const queryString = params ? `?${new URLSearchParams(params).toString()}` : '';
            const result = await fetchWrapper(`${PUBLIC_ENDPOINT}/tenants/${tenantId}${queryString}`);
            return { data: result, error: result?.error };
        },
    },

    actions: {
        execute: async (action: string, payload: any) => {
            const query = payload ? `?${new URLSearchParams(payload).toString()}` : '';
            const result = await fetchWrapper(`/actions/${action}${query}`);
            return { data: result, error: result?.error };
        },
    },

    users: {
        getAll: createGetAllMethod('/users'),
        getOne: createGetOneMethod('/users'),
        save: createSaveMethod('/users'),
        delete: createDeleteMethod('/users'),
        emailHasUser: async (email: string) => {
            const result = await fetchWrapper(`/users/email-has-user?email=${encodeURIComponent(email)}`);
            return { data: result, error: result?.error };
        },
    },

    tenants: {
        getOne: createGetOneMethod('/tenants'),
        save: createSaveMethod('/tenants', 'operation.save_settings'),
    },

    afip: {
        createCert: async (data: any) => {
            const result = await fetchWrapper('/afip/create-cert', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return result;
        },
        authService: async (data: any) => {
            const result = await fetchWrapper('/afip/auth-service', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return result;
        },
        createSalesPoint: async (data: any) => {
            const result = await fetchWrapper('/afip/create-sales-point', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return result;
        },
        syncSalesPoints: async (data?: any) => {
            const result = await fetchWrapper('/afip/sync-sales-points', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data || {})
            });
            return result;
        },
        generateInvoice: async (data: any) => {
            const result = await fetchWrapper('/afip/generate-invoice', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return result;
        },
        getVouchers: async (params?: { direction?: 'emitted' | 'received' | 'all'; issuer_business_id?: number; service_booking_id?: number; reservation_id?: number }): Promise<AfipVoucher[]> => {
            const query = new URLSearchParams();
            if (params?.direction) query.set('direction', params.direction);
            if (params?.issuer_business_id) query.set('issuer_business_id', String(params.issuer_business_id));
            if (params?.service_booking_id) query.set('service_booking_id', String(params.service_booking_id));
            if (params?.reservation_id) query.set('reservation_id', String(params.reservation_id));
            const qs = query.toString();
            const result = await fetchWrapper(`/afip/vouchers${qs ? `?${qs}` : ''}`, {
                method: 'GET'
            });
            return result;
        },
        createReceivedVoucher: async (data: any): Promise<AfipVoucher> => {
            const result = await fetchWrapper('/afip/received-vouchers', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return result;
        },
        updateReceivedVoucher: async (voucherId: number | string, data: any): Promise<AfipVoucher> => {
            const result = await fetchWrapper(`/afip/received-vouchers/${voucherId}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data)
            });
            return result;
        },
        deleteReceivedVoucher: async (voucherId: number | string) => {
            const result = await fetchWrapper(`/afip/received-vouchers/${voucherId}`, {
                method: 'DELETE'
            });
            return result;
        },
        getServiceBookingVouchers: async (serviceBookingId: number | string): Promise<AfipVoucher[]> => {
            const result = await fetchWrapper(`/service-bookings/${serviceBookingId}/afip-vouchers`, {
                method: 'GET'
            });
            return result;
        },
        linkVoucherToServiceBooking: async (serviceBookingId: number | string, voucherId: number | string, amount?: number | null) => {
            const result = await fetchWrapper(`/service-bookings/${serviceBookingId}/afip-vouchers/${voucherId}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ amount })
            });
            return result;
        },
        unlinkVoucherFromServiceBooking: async (serviceBookingId: number | string, voucherId: number | string) => {
            const result = await fetchWrapper(`/service-bookings/${serviceBookingId}/afip-vouchers/${voucherId}`, {
                method: 'DELETE'
            });
            return result;
        },
        sendVoucherEmail: async (voucherId: number | string, sendTo: string | string[]) => {
            const result = await fetchWrapper(`/afip/vouchers/${voucherId}/send-email`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ send_to: sendTo })
            });
            return result;
        }
    },

    templates: {
        getAll: createGetAllMethod('/templates'),
        getOne: createGetOneMethod('/templates'),
        save: createSaveMethod('/templates', 'operation.save_template'),
        delete: createDeleteMethod('/templates'),
    },

    emailTemplates: {
        getAll: createGetAllMethod('/email-templates'),
        getOne: createGetOneMethod('/email-templates'),
        save: createSaveMethod('/email-templates', 'operation.save_email_template'),
        delete: createDeleteMethod('/email-templates'),
    },

    savedReports: {
        getAll: createGetAllMethod('/saved-reports'),
        getOne: createGetOneMethod('/saved-reports'),
        save: createSaveMethod('/saved-reports', 'operation.save_saved_report'),
        delete: createDeleteMethod('/saved-reports'),
    },

    businesses: {
        getAll: createGetAllMethod('/businesses'),
        getOne: createGetOneMethod('/businesses'),
        getMyBusinessesHome: async () => {
            const result = await fetchWrapper('/businesses/my-businesses-home');
            return { data: result?.data || result, error: result?.error };
        },
        getVendorsWithPublicServices: async () => {
            const result = await fetchWrapper('/businesses-with-public-services');
            return { data: result, error: result?.error };
        },
        getClientReservations: async (businessId: number | string) => {
            const result = await fetchWrapper(`/businesses/${businessId}/reservations-as-client`);
            return { data: result?.data || result, error: result?.error };
        },
        save: createSaveMethod('/businesses', 'operation.save_business'),
        delete: createDeleteMethod('/businesses'),
        merge: async (targetBusinessId: number | string, sourceBusinessId: number | string) => {
            const result = await fetchWrapper(`/businesses/${targetBusinessId}/merge`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ sourceBusinessId }),
            });
            return { data: result?.data || result, error: result?.error };
        },
    },

    tenantBusinesses: {
        getAll: createGetAllMethod('/tenant-businesses'),
        getOne: createGetOneMethod('/tenant-businesses'),
        getTenantBusiness: async (tenantId: number | string, businessId: number | string) => {
            const result = await fetchWrapper(`/tenant-businesses/getTenantBusiness?tenant_id=${tenantId}&business_id=${businessId}`);
            return { data: result, error: result?.error };
        },
        save: createSaveMethod('/tenant-businesses', 'operation.savetenant_business'),
        delete: createDeleteMethod('/tenant-businesses'),
        sendTariffsEmail: async (id: string | number) => {
            const result = await fetchWrapper(`/tenant-businesses/${id}/send-tariffs-email`, {
                method: 'POST',
            });
            return { data: result, error: result?.error };
        },
        sendBulkEmail: async (data: { businessIds?: (string | number)[] | 'all'; templateId?: string | number; manualEmails?: string[]; fromEmail?: string }) => {
            const result = await fetchWrapper(`/tenant-businesses/send-bulk-email`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        sendBulkTariffsEmail: async (data: { businessIds?: (string | number)[] | 'all'; templateId?: string | number; manualEmails?: string[]; fromEmail?: string }) => {
            const result = await fetchWrapper(`/tenant-businesses/send-bulk-email`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        sendAssignmentsEmail: async (data: { businessId: string | number; date: string; manualEmails?: string[]; reservationsCount?: number; fromEmail?: string }) => {
            const result = await fetchWrapper(`/tenant-businesses/send-assignments-email`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        resetRanks: async () => {
            const result = await fetchWrapper(`/tenant-business-ranks/reset`);
            return { data: result, error: result?.error };
        },
        getTariffEmailLogs: async (id: string | number) => {
            const result = await fetchWrapper(`/tenant-businesses/${id}/email-logs`);
            return { data: result?.data, error: result?.error };
        },
        getAllTariffEmailLogs: async () => {
            const result = await fetchWrapper(`/email-logs`);
            return { data: result?.data, error: result?.error };
        },
    },


    deletedReservations: {
        getAll: createGetAllMethod('/deleted-reservations'),
        getOne: createGetOneMethod('/deleted-reservations'),
    },

    reservations: {
        getAll: createGetAllMethod('/reservations'),
        getTotalSold: async (params?: Record<string, any>) => {
            const query = params ? `?${new URLSearchParams(Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '') as [string, string][]).toString()}` : '';
            const result = await fetchWrapper(`/reservations/total-sold${query}`);
            return { data: result, error: result?.error };
        },
        agenda: createGetAllMethod('/agenda'),
        getAssignedCoordinator: createGetAllMethod('/assignments/coordinator-reservations'),
        getOne: createGetOneMethod('/reservations'),
        getErrorState: async (id: number | string) => {
            const result = await fetchWrapper(`/reservations/${id}/error-state`, {
                method: 'GET',
            });
            return { data: result, error: result?.error };
        },
        save: createSaveMethod('/reservations', 'operation.save_reservation'),
        delete: createDeleteMethod('/reservations'),
        duplicate: async (id: number | string, data?: { client_id?: number; client_type?: string }) => {
            const result = await fetchWrapper(`/reservations/${id}/duplicate`, {
                method: 'POST',
                body: data ? JSON.stringify(data) : undefined,
            });
            return { data: result, error: result?.error };
        },
        sendTripReservationEmail: async (id: number | string, data: { emails: string[]; subject?: string; messageId?: string; fromEmail?: string; customMessage?: string }) => {
            const result = await fetchWrapper(`/reservations/${id}/send-trip-email`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        getEmailLogs: async (id: number | string) => {
            const result = await fetchWrapper(`/reservations/${id}/email-logs`, {
                method: 'GET',
            });
            return { data: result?.data, error: result?.error };
        },
        getEmailThreads: async (id: number | string) => {
            const result = await fetchWrapper(`/reservations/${id}/email-threads`, {
                method: 'GET',
            });
            return { data: result?.data, error: result?.error };
        },
        getEmailThreadDetails: async (id: number | string, threadId: string) => {
            const result = await fetchWrapper(`/reservations/${id}/email-threads/${threadId}`, {
                method: 'GET',
            });
            return { data: result?.data, error: result?.error };
        },
        getEmailAttachmentUrl: (id: number | string, messageId: string, attachmentId: string, filename?: string, mimeType?: string, inline?: boolean) => {
            const params = new URLSearchParams({
                messageId,
                attachmentId,
                ...(filename ? { filename } : {}),
                ...(mimeType ? { mimeType } : {}),
                ...(inline ? { inline: 'true' } : {})
            });
            return `${BASE_URL}/reservations/${id}/email-threads/attachment?${params.toString()}`;
        },
        downloadEmailAttachment: async (id: number | string, messageId: string, attachmentId: string, filename: string, mimeType?: string) => {
            const params = new URLSearchParams({
                messageId,
                attachmentId,
                filename,
                ...(mimeType ? { mimeType } : {})
            });
            const url = `${BASE_URL}/reservations/${id}/email-threads/attachment?${params.toString()}`;
            const response = await fetch(url, {
                method: 'GET',
                credentials: 'include'
            });
            if (!response.ok) {
                throw new Error('failed_to_download_attachment');
            }
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = downloadUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(downloadUrl);
        },
        linkEmailThread: async (id: number | string, data: { gmail_thread_id: string; google_account_id?: number; subject?: string; snippet?: string }) => {
            const result = await fetchWrapper(`/reservations/${id}/email-threads`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result?.data, error: result?.error };
        },
        unlinkEmailThread: async (id: number | string, threadId: string) => {
            const result = await fetchWrapper(`/reservations/${id}/email-threads/${threadId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        replyEmailThread: async (id: number | string, threadId: string, data: { to: string; cc?: string; subject: string; bodyHtml: string; bodyText?: string; replyAll?: boolean; inReplyTo?: string; references?: string }) => {
            const result = await fetchWrapper(`/reservations/${id}/email-threads/${threadId}/reply`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result?.data, error: result?.error };
        },
        composeEmailThread: async (id: number | string, data: { to: string; cc?: string; subject: string; bodyHtml: string }) => {
            const result = await fetchWrapper(`/reservations/${id}/email-threads/compose`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result?.data, error: result?.error };
        },
        assignClient: async (id: number | string, clientId: number | null, clientType: 'person' | 'business' | null) => {
            const result = await fetchWrapper(`/reservations/${id}/reservation-client`, {
                method: 'PUT',
                body: JSON.stringify({ clientId, clientType }),
            });
            return { data: result, error: result?.error };
        },
        deleteServiceBooking: createDeleteMethod('/service-bookings'),
        getServiceBooking: createGetOneMethod('/service-bookings'),
        saveServiceBooking: createSaveMethod('/service-bookings', 'operation.save_service_booking'),
        updateSelfAttr: async (id: string | number, key: string, value: any) => {
            const result = await fetchWrapper(`/service-bookings/${id}/self-attr`, {
                method: 'PUT',
                body: JSON.stringify({ key, value }),
            });
            return result;
        },
        getUnpaid: async (clientId: number, clientType: string) => {
            const result = await fetchWrapper(`/unpaid-reservations?client_id=${clientId}&client_type=${clientType}`);
            return { data: result?.data || result, error: result?.error };
        },
        splitPax: async (reservationId: number | string, paxId: number | string, newPaxs: any[]) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/pax-split`, {
                method: 'POST',
                body: JSON.stringify({ paxId, newPaxs }),
            });
            return { data: result, error: result?.error };
        },
        addPriceModifier: async (reservationId: number | string, data: any) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/price-modifiers`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        deletePriceModifier: async (id: number | string) => {
            const result = await fetchWrapper(`/reservation-price-modifiers/${id}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        getPriceModifiers: async (reservationId: number | string) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/price-modifiers`);
            return { data: result?.rows || result?.data || result, error: result?.error };
        },
    },

    serviceBookings: {
        getUnpaidCosts: async (providerId: number) => {
            const result = await fetchWrapper(`/unpaid-service-bookings?provider_id=${providerId}`);
            return { data: result?.data || result, error: result?.error };
        },
        save: createSaveMethod('/service-bookings', 'operation.save_service_booking'),
        saveServiceOrder: createSaveMethod('/service-bookings/service-order', 'operation.save_service_booking'),
        updateMoney: async (id: number | string, data: any) => {
            const result = await fetchWrapper(`/service-bookings/${id}/price`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            notify.smart({ data: result }, 'operation.save_service_booking');
            return { data: result, error: result?.error };
        },
        resetPrice: async (id: number | string) => {
            const result = await fetchWrapper(`/service-bookings/${id}/reset-price`, {
                method: 'PUT',
            });
            notify.smart({ data: result }, 'operation.reset_service_booking_price');
            return { data: result, error: result?.error };
        },
        resetCost: async (id: number | string) => {
            const result = await fetchWrapper(`/service-bookings/${id}/reset-cost`, {
                method: 'PUT',
            });
            notify.smart({ data: result }, 'operation.reset_service_booking_cost');
            return { data: result, error: result?.error };
        },
        updateModifiers: async (id: number | string, data: any[] | { applied_money_mods?: any[]; applied_money_out_mods?: any[] }) => {
            const body = Array.isArray(data) ? { applied_money_mods: data } : data;
            const result = await fetchWrapper(`/service-bookings/${id}/modifiers`, {
                method: 'PUT',
                body: JSON.stringify(body),
            });
            notify.smart({ data: result }, 'operation.save_service_booking');
            return { data: result, error: result?.error };
        },
        updateServiceData: async (id: number | string) => {
            const result = await fetchWrapper(`/service-bookings/${id}/service-data`, {
                method: 'PUT',
            });
            notify.smart({ data: result }, 'operation.save_service_booking');
            return { data: result, error: result?.error };
        },
        setInventoryItems: async (bookingId: number | string, inventoryItemIds: (number | string)[]) => {
            const result = await fetchWrapper(`/service-bookings/${bookingId}/inventory-items`, {
                method: 'PUT',
                body: JSON.stringify({ inventory_item_ids: inventoryItemIds }),
            });
            return { data: result, error: result?.error };
        },
        getAssigned: createGetAllMethod('/assignments/bookings'),
    },

    serviceBookingInventoryLinks: {
        get: async (params?: any) => {
            const query = params ? `?${new URLSearchParams(cleanParams(params)).toString()}` : '';
            const result = await fetchWrapper(`/service-booking-inventory-items${query}`);
            return { data: result, error: result?.error }; // Response is array of links
        },
        create: async (data: { service_booking_id: number | string, inventory_item_id: number | string }) => {
            const result = await fetchWrapper(`/service-booking-inventory-items`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        delete: async (bookingId: number | string, itemId: number | string) => {
            const result = await fetchWrapper(`/service-bookings/${bookingId}/inventory-items/${itemId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        }
    },

    serviceProviders: {
        getAll: createGetAllMethod('/service-providers'),
        getOne: createGetOneMethod('/service-providers'),
        save: createSaveMethod('/service-providers'),
        delete: createDeleteMethod('/service-providers'),
    },

    serviceLinkedServices: {
        getAll: createGetAllMethod('/service-linked-services'),
        getOne: createGetOneMethod('/service-linked-services'),
        save: createSaveMethod('/service-linked-services'),
        delete: createDeleteMethod('/service-linked-services'),
    },

    services: {
        getLocations: async (category?: string) => {
            const query = category ? `?category=${encodeURIComponent(category)}` : '';
            const result = await fetchWrapper(`/locations${query}`);
            return { data: Array.isArray(result) ? result : (result?.data || []), error: result?.error };
        },
        getFilterOptions: async (params?: any) => {
            const endpoint = params ? `/services/filter-options?${new URLSearchParams(cleanParams(params)).toString()}` : '/services/filter-options';
            const result = await fetchWrapper(endpoint);
            return { data: result, error: result?.error };
        },
        getAll: createGetAllMethod('/services'),
        getOne: createGetOneMethod('/services'),
        getByProvider: async (businessId: string | number) => {
            const result = await fetchWrapper(`/services/by-provider/${businessId}`);
            return { data: result?.rows, error: result?.error };
        },
        save: createSaveMethod('/services', 'operation.save_service'),
        delete: createDeleteMethod('/services'),
        getBookings: createGetAllMethod('/service-bookings'),
        getBookingsByService: createGetAllMethod('/service-bookings-by-service'),
        getAvailableLimits: async (params?: any) => {
            const endpoint = params ? `/service-available-limits?${new URLSearchParams(params).toString()}` : '/service-available-limits';
            const result = await fetchWrapper(endpoint);
            return { data: result, error: result?.error };
        },
        getBookingsLimits: async (params?: any) => {
            const endpoint = params ? `/service-bookings-limits?${new URLSearchParams(params).toString()}` : '/service-bookings-limits';
            const result = await fetchWrapper(endpoint);
            return { data: result, error: result?.error };
        },
        getInventory: async (serviceId: string | number) => {
            const result = await fetchWrapper(`/services/${serviceId}/inventory`);
            return { data: result, error: result?.error };
        },
        updateInventory: async (serviceId: string | number, data: any) => {
            const result = await fetchWrapper(`/services/${serviceId}/inventory`, {
                method: 'PUT',
                body: JSON.stringify(data)
            });
            return { data: result, error: result?.error };
        },
        getCategories: createGetAllMethod('/services-categories'),
        resetRank: async () => {
            const result = await fetchWrapper(`/service-ranks/reset`);
            return { data: result, error: result?.error };
        },
        moveCategoryRankUp: async (id: string | number) => {
            const result = await fetchWrapper(`/service-category-ranks/up/${id}`);
            return { data: result, error: result?.error };
        },
        moveCategoryRankDown: async (id: string | number) => {
            const result = await fetchWrapper(`/service-category-ranks/down/${id}`);
            return { data: result, error: result?.error };
        },
        resetCategoryRank: async () => {
            const result = await fetchWrapper(`/service-category-ranks/reset`);
            return { data: result, error: result?.error };
        },
        addChildService: async (serviceId: number | string, childServiceId: number | string) => {
            const result = await fetchWrapper(`/services/${serviceId}/child-services/${childServiceId}`, {
                method: 'POST'
            });
            return { data: result, error: result?.error };
        },
        removeChildService: async (serviceId: number | string, childServiceId: number | string) => {
            const result = await fetchWrapper(`/services/${serviceId}/child-services/${childServiceId}`, {
                method: 'DELETE'
            });
            return { data: result, error: result?.error };
        },
        getCategoriesPrivate: createGetAllMethod('/services-categories'),
    },

    serviceGroups: {
        getAll: createGetAllMethod('/service-groups'),
        getOne: createGetOneMethod('/service-groups'),
        save: createSaveMethod('/service-groups', 'operation.save_service_group'),
        delete: createDeleteMethod('/service-groups'),
        addServiceToGroup: async (groupId: number | string, serviceId: number | string, discount: number = 0) => {
            const result = await fetchWrapper(`/service-groups/${groupId}/services/${serviceId}`, {
                method: 'POST',
                body: JSON.stringify({ discount }),
            });
            return { data: result, error: result?.error };
        },
        updateServiceLinkDiscount: async (linkId: number | string, discount: number) => {
            const result = await fetchWrapper(`/service-groups-service-discount/${linkId}`, {
                method: 'PUT',
                body: JSON.stringify({ discount }),
            });
            return { data: result, error: result?.error };
        },
        removeServiceFromGroup: async (groupId: number | string, serviceId: number | string) => {
            const result = await fetchWrapper(`/service-groups/${groupId}/services/${serviceId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        removeCover: async (groupId: number | string) => {
            const result = await fetchWrapper(`/service-groups/${groupId}/cover`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
    },

    products: {
        getAll: createGetAllMethod('/products'),
        getOne: createGetOneMethod('/products'),
        save: createSaveMethod('/products', 'operation.save_product'),
        delete: createDeleteMethod('/products'),
    },

    serviceSellPrices: {
        getAll: createGetAllMethod('/service-sell-prices'),
        getOne: createGetOneMethod('/service-sell-prices'),
        save: createSaveMethod('/service-sell-prices', 'operation.save_service_sell_price'),
        delete: createDeleteMethod('/service-sell-prices'),
        getActive: createGetAllMethod('/service-sell-prices/active'),
    },

    serviceSellCosts: {
        getAll: createGetAllMethod('/service-sell-costs'),
        getOne: createGetOneMethod('/service-sell-costs'),
        save: createSaveMethod('/service-sell-costs', 'operation.save_service_sell_cost'),
        delete: createDeleteMethod('/service-sell-costs'),
        getActive: createGetAllMethod('/service-sell-prices/active'),
    },

    serviceOrders: {
        addBookingToOrder: async (serviceOrderId: number | string, serviceBookingId: number | string) => {
            const result = await fetchWrapper(`/service-orders/${serviceOrderId}/service-bookings/${serviceBookingId}`, {
                method: 'POST',
            });
            return { data: result, error: result?.error };
        },
        removeBookingFromOrder: async (serviceOrderId: number | string, serviceBookingId: number | string) => {
            const result = await fetchWrapper(`/service-orders/${serviceOrderId}/service-bookings/${serviceBookingId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        linkServiceOrderToInventoryItem: async (serviceOrderId: number | string, inventoryItemId: number | string) => {
            const result = await fetchWrapper(`/service-orders/${serviceOrderId}/inventory-items/${inventoryItemId}`, {
                method: 'POST',
            });
            return { data: result, error: result?.error };
        },
        unlinkServiceOrderToInventoryItem: async (linkId: number | string) => {
            const result = await fetchWrapper(`/service-orders-inventory-items/${linkId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        saveServiceOrder: createSaveMethod('/service-orders', 'operation.save_vehicle_booking'),
        deleteServiceOrder: createDeleteMethod('/service-orders'),
        getServiceOrder: createGetOneMethod('/service-orders'),
        getServiceBookings: createGetAllMethod('/service-orders'),
        updateServiceOrderBooking: async (id: number | string, data: any) => {
            const result = await fetchWrapper(`/service-orders-bookings/${id}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        getAssigned: createGetAllMethod('/assignments/service-orders'),
    },

    vehicles: {
        getAll: createGetAllMethod('/vehicles'),
        getAllWithBookings: createGetAllMethod('/vehicles-with-bookings'),
        getOne: createGetOneMethod('/vehicles'),
        save: createSaveMethod('/vehicles', 'operation.save_vehicle'),
        delete: createDeleteMethod('/vehicles'),
    },

    persons: {
        getAll: createGetAllMethod('/persons'),
        getOne: createGetOneMethod('/persons'),
        save: createSaveMethod('/persons', 'operation.save_person'),
        bulkCreate: async (payload: string | Partial<Person>[]) => {
            const body = typeof payload === 'string' ? { csv: payload } : { persons: payload };
            const result = await fetchWrapper('/persons/bulk', {
                method: 'POST',
                body: JSON.stringify(body),
            });
            return {
                data: (result?.data !== undefined ? result.data : result) as Person[],
                messages: result?.messages as Array<{
                    row?: number;
                    type: 'error' | 'warning' | 'info';
                    code: string;
                    params?: Record<string, any>;
                    line?: string;
                }> | undefined,
                error: result?.error,
            };
        },
        delete: createDeleteMethod('/persons'),
        getAllStaff: async () => {
            const result = await fetchWrapper('/persons-staff');
            return { data: result?.rows, count: result?.count, error: result?.error };
        },
        addToReservation: async (reservationId: number, personId: number) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/persons/${personId}`, {
                method: 'POST',
            });
            return { data: result, error: result?.error };
        },
        removeFromReservation: async (reservationId: number, personId: number) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/persons/${personId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        addToServiceBooking: async (
            serviceBookingId: number,
            personId: number,
            paxCount?: number | { adults?: number; children?: number; infants?: number; seniors?: number; guests?: number }
        ) => {
            const bodyObj = typeof paxCount === 'object' ? paxCount : { pax_count: paxCount };
            const result = await fetchWrapper(`/service-bookings/${serviceBookingId}/persons/${personId}`, {
                method: 'POST',
                body: JSON.stringify(bodyObj)
            });
            return { data: result, error: result?.error };
        },
        removeFromServiceBooking: async (serviceBookingId: number, personId: number) => {
            const result = await fetchWrapper(`/service-bookings/${serviceBookingId}/persons/${personId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        addToServiceOrder: async (serviceOrderId: number, personId: number) => {
            const result = await fetchWrapper(`/service-orders/${serviceOrderId}/persons/${personId}`, {
                method: 'POST',
            });
            return { data: result, error: result?.error };
        },
        removeFromServiceOrder: async (serviceOrderId: number, personId: number) => {
            const result = await fetchWrapper(`/service-orders/${serviceOrderId}/persons/${personId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        getReservationsAsPax: async (personId: number | string) => {
            const result = await fetchWrapper(`/persons/${personId}/reservations-as-pax`);
            return { data: result?.data !== undefined ? result.data : result, error: result?.error };
        },
    },

    paxGroups: {
        getByReservation: async (reservationId: number | string) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/pax-groups`);
            return { data: result?.data !== undefined ? result.data : result, error: result?.error };
        },
        create: async (reservationId: number | string, data: Partial<ReservationPaxGroup>) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/pax-groups`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result?.data !== undefined ? result.data : result, error: result?.error };
        },
        update: async (reservationId: number | string, groupId: number | string, data: Partial<ReservationPaxGroup>) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/pax-groups/${groupId}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return { data: result?.data !== undefined ? result.data : result, error: result?.error };
        },
        delete: async (reservationId: number | string, groupId: number | string) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/pax-groups/${groupId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        addToAllBookings: async (reservationId: number | string, groupId: number | string) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/pax-groups/${groupId}/add-to-all-bookings`, {
                method: 'POST',
            });
            return { data: result, error: result?.error };
        },
        removeFromAllBookings: async (reservationId: number | string, groupId: number | string) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/pax-groups/${groupId}/remove-from-all-bookings`, {
                method: 'POST',
            });
            return { data: result, error: result?.error };
        },
        assignToServiceBooking: async (serviceBookingId: number | string, groupId: number | string, data?: Partial<ServiceBookingPaxGroup>) => {
            const result = await fetchWrapper(`/service-bookings/${serviceBookingId}/pax-groups/${groupId}`, {
                method: 'POST',
                body: JSON.stringify(data || {}),
            });
            return { data: result, error: result?.error };
        },
        removeFromServiceBooking: async (serviceBookingId: number | string, groupId: number | string) => {
            const result = await fetchWrapper(`/service-bookings/${serviceBookingId}/pax-groups/${groupId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        bulkAssignToBookings: async (
            reservationId: number | string,
            assignments: Array<{
                service_booking_id: number;
                pax_group_id: number;
                adults?: number;
                children?: number;
                seniors?: number;
                infants?: number;
                guests?: number;
                is_custom_config?: boolean;
                person_ids?: number[];
                unassign?: boolean;
            }>
        ) => {
            const result = await fetchWrapper(`/reservations/${reservationId}/pax-groups/bulk-assign`, {
                method: 'POST',
                body: JSON.stringify({ assignments }),
            });
            return { data: result, error: result?.error };
        },
    },

    clients: {
        search: async (params: { query: string; categories?: string; owned_by_me?: boolean }) => {
            const query = `?${new URLSearchParams(cleanParams(params)).toString()}`;
            const result = await fetchWrapper(`/clients/search${query}`);
            return { data: result?.data || result, error: result?.error };
        },
        latestsUsed: async (params: { categories?: string; owned_by_me?: boolean }) => {
            const query = `?${new URLSearchParams(cleanParams(params)).toString()}`;
            const result = await fetchWrapper(`/clients/latests-used${query}`);
            return { data: result?.data || result, error: result?.error };
        }
    },

    inventoryItems: {
        getAll: createGetAllMethod('/inventory-items'),
        getOne: createGetOneMethod('/inventory-items'),
        save: createSaveMethod('/inventory-items', 'operation.save_inventory_item'),
        delete: createDeleteMethod('/inventory-items'),
        getStatus: async (params?: { start_date?: string; end_date?: string; days?: number }) => {
            const query = params ? `?${new URLSearchParams(params as any).toString()}` : '';
            const result = await fetchWrapper(`/inventory-status${query}`);
            return { data: result, error: result?.error };
        },
    },


    moneyTransactions: {
        getAll: createGetAllMethod('/money-transactions'),
        getAllIn: createGetAllMethod('/money-transactions/in'),
        getAllOut: createGetAllMethod('/money-transactions/out'),
        getOne: createGetOneMethod('/money-transactions'),
        save: createSaveMethod('/money-transactions', 'operation.save_money_transaction'),
        delete: createDeleteMethod('/money-transactions'),
    },

    businessBalance: {
        getAll: createGetAllMethod('/client-balance'),
    },

    clientHistory: {
        getAll: createGetAllMethod('/client-history'),
    },

    personStatement: {
        getAll: createGetAllMethod('/person-statement'),
    },

    exchangeRates: {
        getAll: createGetAllMethod('/exchange-rates'),
        getOne: createGetOneMethod('/exchange-rates'),
        save: createSaveMethod('/exchange-rates', 'operation.save_exchange_rate'),
        delete: createDeleteMethod('/exchange-rates'),
    },

    inputSuggestions: {
        get: async (params: { type: string; id: string | number; key: string }) => {
            const query = `?${new URLSearchParams(cleanParams(params)).toString()}`;
            const result = await fetchWrapper(`/input-suggestions${query}`);
            return { data: result, error: result?.error };
        },
    },

    discountCategories: {
        getAll: createGetAllMethod('/discount-categories'),
        getOne: createGetOneMethod('/discount-categories'),
        save: createSaveMethod('/discount-categories', 'operation.save_discount_category'),
        delete: createDeleteMethod('/discount-categories'),
    },

    systemLogs: {
        getAll: createGetAllMethod('/api-logs'),
    },

    activities: {
        getAll: createGetAllMethod('/activities'),
    },

    tickets: {
        getAll: createGetAllMethod('/tickets'),
        getOne: createGetOneMethod('/tickets'),
        save: createSaveMethod('/tickets', 'ticket'),
        delete: createDeleteMethod('/tickets'),
        createComment: async (ticketId: number | string, data: any) => {
            const result = await fetchWrapper(`/tickets/${ticketId}/comments`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
    },

    homeFeed: {
        get: async () => {
            const result = await fetchWrapper('/home-feed');
            return { data: result?.data, error: result?.error };
        },
    },

    sharedWithMe: {
        get: async () => {
            const result = await fetchWrapper('/shared-with-me');
            return { data: result?.data, error: result?.error };
        },
        getOne: async (resourceType: string, resourceId: number | string) => {
            const result = await fetchWrapper(`/shared-with-me/${resourceType}/${resourceId}`);
            return { data: result?.data || result, permission_level: result?.permission_level, error: result?.error };
        },
    },

    share: {
        share: async (data: { resourceType: string; resourceId: number; clientType?: string; clientId?: number; userId?: number; level?: string }) => {
            const result = await fetchWrapper('/share', {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        unshare: async (resourceType: string, resourceId: number, userId: number) => {
            const result = await fetchWrapper(`/share/${resourceType}/${resourceId}/user/${userId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        list: async (resourceType: string, resourceId: number) => {
            const result = await fetchWrapper(`/share/${resourceType}/${resourceId}`);
            return { data: result, error: result?.error };
        }
    },

    aiAgent: {
        chat: async (data: { message: string, sessionId?: string }) => {
            const result = await fetchWrapper('/ai-agent/chat', {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        getHistory: async (sessionId: string) => {
            const result = await fetchWrapper(`/ai-agent/history?sessionId=${sessionId}`);
            return { data: result, error: result?.error };
        },
        getSessions: async () => {
            const result = await fetchWrapper('/ai-agent/sessions');
            return { data: result, error: result?.error };
        },
        updateSession: async (id: string | number, data: { title: string }) => {
            const result = await fetchWrapper(`/ai-agent/sessions/${id}`, {
                method: 'PUT',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        deleteSession: async (id: string | number) => {
            const result = await fetchWrapper(`/ai-agent/sessions/${id}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
    },

    subscriptions: {
        getAll: createGetAllMethod('/subscriptions'),
        getOne: createGetOneMethod('/subscriptions'),
        save: createSaveMethod('/subscriptions'),

        delete: createDeleteMethod('/subscriptions'),
        addPayment: async (subscriptionId: number | string, moneyTransactionId: number | string) => {
            const result = await fetchWrapper(`/subscriptions/${subscriptionId}/payments`, {
                method: 'POST',
                body: JSON.stringify({ money_transaction_id: moneyTransactionId }),
            });
            return { data: result, error: result?.error };
        },
        removePayment: async (subscriptionId: number | string, moneyTransactionId: number | string) => {
            const result = await fetchWrapper(`/subscriptions/${subscriptionId}/payments/${moneyTransactionId}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
    },

    payments: {
        getAll: createGetAllMethod('/payments'),
        getOne: createGetOneMethod('/payments'),
        save: createSaveMethod('/payments', 'operation.save_payment'),
        delete: createDeleteMethod('/payments'),
        autoAssignPreview: async (params?: { business_id?: number; payment_id?: number }) => {
            const result = await fetchWrapper('/payments/auto-assign-preview', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params || {})
            });
            return { data: result?.data || result, error: result?.error };
        },
        autoAssignConfirm: async (params?: { business_id?: number; payment_id?: number; assignments?: any[] }) => {
            const result = await fetchWrapper('/payments/auto-assign', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(params || {})
            });
            return { data: result?.data || result, error: result?.error };
        }
    },

    finances: {
        getSummary: async (params?: any) => {
            const query = params ? `?${new URLSearchParams(cleanParams(params)).toString()}` : '';
            const result = await fetchWrapper(`/finances${query}`);
            return { data: result, error: result?.error };
        },
    },

    metrics: {
        getMetrics: async (params?: any) => {
            const query = params ? `?${new URLSearchParams(cleanParams(params)).toString()}` : '';
            const result = await fetchWrapper(`/metrics${query}`);
            return { data: result, error: result?.error };
        },
    },

    notifications: {
        getAll: async (params?: any) => {
            const query = params ? `?${new URLSearchParams(cleanParams(params)).toString()}` : '';
            const result = await fetchWrapper(`/notifications${query}`);
            return {
                data: result?.rows || result?.data,
                count: result?.count,
                unread_count: result?.unread_count,
                error: result?.error
            };
        },
        getOne: createGetOneMethod('/notifications'),
        save: createSaveMethod('/notifications'),
        delete: createDeleteMethod('/notifications'),
        markAllSeen: async () => {
            const result = await fetchWrapper('/notifications/mark-all-seen', {
                method: 'POST',
            });
            return { data: result, error: result?.error };
        },
    },

    balance: {
        getGlobalBusinesses: createGetAllMethod('/balance/global/businesses'),
        getIssues: () => fetchWrapper('/balance/issues'),
        getPersonBalance: (id: string | number, params?: any) => {
            const cleanParams = params ? Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')) : {};
            const query = params ? '?' + new URLSearchParams(cleanParams as Record<string, string>).toString() : '';
            return fetchWrapper(`/balance/persons/${id}${query}`);
        },
        getBusinessBalance: (id: string | number, params?: any) => {
            const cleanParams = params ? Object.fromEntries(Object.entries(params).filter(([_, v]) => v !== undefined && v !== null && v !== '')) : {};
            const query = params ? '?' + new URLSearchParams(cleanParams as Record<string, string>).toString() : '';
            return fetchWrapper(`/balance/businesses/${id}${query}`);
        },
        sendEmail: async (data: { clientId: number | string; clientType: 'business' | 'person'; emails: string[]; startDay?: string; endDay?: string; paymentStatusFilter?: string | string[]; fromEmail?: string; customMessage?: string }) => {
            const result = await fetchWrapper(`/balance/send-email`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        findMatches: async (data: { seller_id: number; client_id: number; client_type: 'business' | 'person' }) => {
            const result = await fetchWrapper(`/balance/reconcile/find-matches`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        },
        executeReconciliation: async (data: { matches: { payment_id: number; reservation_id: number }[] }) => {
            const result = await fetchWrapper(`/balance/reconcile/execute`, {
                method: 'POST',
                body: JSON.stringify(data),
            });
            return { data: result, error: result?.error };
        }
    },

    globalSearch: {
        get: async (query: string, category: string) => {
            const result = await fetchWrapper(`/global-search?query=${query}&category=${category}`);
            return { data: result, error: result?.error };
        }
    },

    trackedFlights: {
        getAll: async (params?: any) => {
            const query = params ? `?${new URLSearchParams(cleanParams(params)).toString()}` : '';
            const result = await fetchWrapper(`/tracked-flights${query}`);
            return { data: result?.rows || result?.data, count: result?.count, error: result?.error };
        },
        getByBooking: async (bookingId: number | string) => {
            const result = await fetchWrapper(`/tracked-flights/booking/${bookingId}`);
            return { data: result?.data, error: result?.error };
        },
        sync: async (bookingId: number | string) => {
            const result = await fetchWrapper(`/tracked-flights/sync/${bookingId}`, {
                method: 'POST',
            });
            return { data: result?.data, error: result?.error };
        },
        syncAll: async () => {
            const result = await fetchWrapper('/tracked-flights/sync', {
                method: 'POST',
            });
            return { data: result?.data, error: result?.error };
        }
    },

    googleIntegration: {
        getAuthUrl: async (redirectUri: string, loginHint?: string) => {
            let endpoint = `/integrations/google/auth-url?redirect_uri=${encodeURIComponent(redirectUri)}`;
            if (loginHint) {
                endpoint += `&login_hint=${encodeURIComponent(loginHint)}`;
            }
            const result = await fetchWrapper(endpoint);
            return { data: result, error: result?.error };
        },
        getAccounts: async () => {
            const result = await fetchWrapper(`/integrations/google/accounts`);
            return { data: result?.data || [], error: result?.error };
        },
        deleteAccount: async (id: number) => {
            const result = await fetchWrapper(`/integrations/google/accounts/${id}`, {
                method: 'DELETE',
            });
            return { data: result, error: result?.error };
        },
        callback: async (code: string, redirectUri: string) => {
            const result = await fetchWrapper(`/integrations/google/callback`, {
                method: 'POST',
                body: JSON.stringify({ code, redirect_uri: redirectUri }),
            });
            return { data: result, error: result?.error };
        },
        getStatus: async () => {
            const result = await fetchWrapper(`/integrations/google/status`);
            return { data: result, error: result?.error };
        },
        disconnect: async () => {
            const result = await fetchWrapper(`/integrations/google/disconnect`, {
                method: 'POST',
            });
            return { data: result, error: result?.error };
        },
        search: async (query: string, limit: number = 15, accountId?: number) => {
            const accountQuery = accountId ? `&account_id=${accountId}` : '';
            const result = await fetchWrapper(`/integrations/google/search?q=${encodeURIComponent(query)}&limit=${limit}${accountQuery}`);
            return { data: result?.data, error: result?.error };
        },
    },
};

export default api;
